import {
  useCurrencySymbol,
  useFeatureFlag,
  useReferralRole,
  useRegion,
} from "hooks"
import { colours } from "@shared/ui.design-system"
import React, { useEffect } from "react"
import { RafBanner } from "packages/acquisition/components/chakra-components/ReferAFriend/RafBanner/RafBanner"
import type { SkinnyBanner as SkinnyBannerType } from "types"
import type { LocaleType } from "lib/apollo/types"
import { useExperimentViewed } from "tracking/hooks/useExperimentViewed"
import { ExperimentFlags } from "@shared/utils.experimentation"
import { BannerText } from "./BannerText"
import { SkinnyBanner } from "./SkinnyBanner"
import { useSkinnyBannerItem } from "./useSkinnyBannerItem"

type Props = {
  skinnyBannerData: SkinnyBannerType[]
  locale: LocaleType
  loadingBannerData: boolean
}

const freeShippingFeatureMap = {
  "1": { uk: 50, us: 50 },
  "2": { uk: 25, us: 30 },
}

const MEMBER_SKINNY_BANNER_IDS = [
  "7FD9DE9yZDVIvq3t0Urvd9",
  "6WbFDVdp0FBQVz9AAuf9nE",
]

export const BannerContainer: React.FC<Props> = ({
  skinnyBannerData,
  locale,
  loadingBannerData,
}) => {
  const bestsellers =
    locale === "en-US" ? "/us/shop/bestsellers" : "/shop/bestsellers"
  const referralRole = useReferralRole()
  const bannerData = useSkinnyBannerItem(skinnyBannerData)
  const currencySymbol = useCurrencySymbol()

  const trackExperimentViewed = useExperimentViewed()
  const store = useRegion()

  const [loading, freeShippingFeatureFlag] = useFeatureFlag(
    ExperimentFlags.USE_FREE_SHIPPING_THRESHOLD,
    "0"
  )

  useEffect(() => {
    const { experimentId, selectedVariantIndex } = bannerData ?? {}
    if (!experimentId || selectedVariantIndex === undefined) {
      return
    }
    trackExperimentViewed(experimentId, selectedVariantIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData?.experimentId, bannerData?.selectedVariantIndex])

  useEffect(() => {
    if (
      !bannerData?.id ||
      !MEMBER_SKINNY_BANNER_IDS.includes(bannerData?.id) ||
      freeShippingFeatureFlag === "0"
    ) {
      return
    }
    trackExperimentViewed(
      ExperimentFlags.USE_FREE_SHIPPING_THRESHOLD,
      parseFloat(freeShippingFeatureFlag)
    )
  }, [bannerData?.id, freeShippingFeatureFlag, trackExperimentViewed])

  if (referralRole) {
    return <RafBanner referralRole={referralRole} />
  }

  if (loadingBannerData) {
    return (
      <SkinnyBanner
        key="SkinnyBanner"
        href=""
        backgroundColor={colours.warning.medium}
      >
        <></>
      </SkinnyBanner>
    )
  }

  if (!bannerData) return null

  const {
    id,
    title,
    href,
    backgroundColor,
    longText,
    shortText,
    preventWidowsDesktop,
    preventWidowsMobile,
    countdown,
  } = bannerData

  const expiration = countdown?.countdownEndDate ?? undefined

  // we are waiting for the feature to load, so we do not see the free shipping price flicking between 25 and default 50
  if (MEMBER_SKINNY_BANNER_IDS.includes(id) && loading) {
    return <></>
  }

  // we are only overriding the free shipping banner
  if (
    MEMBER_SKINNY_BANNER_IDS.includes(id) &&
    freeShippingFeatureFlag !== "0"
  ) {
    return (
      <SkinnyBanner
        key={`SkinnyBanner-${title}`}
        href={href ?? bestsellers}
        backgroundColor={backgroundColor ?? colours.accent.medium}
        expirationDate={expiration}
      >
        {longText && (
          <BannerText
            id="SkinnyBannerDesktopText"
            text={`<p>FREE STANDARD DELIVERY ON ALL ORDERS ${currencySymbol}${freeShippingFeatureMap[freeShippingFeatureFlag][store]}+</p>`}
            preventWidows={preventWidowsDesktop}
          />
        )}
        {shortText && (
          <BannerText
            id="SkinnyBannerMobileText"
            text={`<p>FREE STANDARD DELIVERY ON ALL ORDERS ${currencySymbol}${freeShippingFeatureMap[freeShippingFeatureFlag][store]}+</p>`}
            preventWidows={preventWidowsMobile}
          />
        )}
      </SkinnyBanner>
    )
  }

  return (
    <SkinnyBanner
      key={`SkinnyBanner-${title}`}
      href={href ?? bestsellers}
      backgroundColor={backgroundColor ?? colours.accent.medium}
      expirationDate={expiration}
    >
      {longText && (
        <BannerText
          id="SkinnyBannerDesktopText"
          text={longText}
          preventWidows={preventWidowsDesktop}
        />
      )}
      {shortText && (
        <BannerText
          id="SkinnyBannerMobileText"
          text={shortText}
          preventWidows={preventWidowsMobile}
        />
      )}
    </SkinnyBanner>
  )
}
